import { useId, useRef, useState, type ReactNode } from "react";
import { Loader2, Upload } from "lucide-react";

/**
 * Einheitliche Upload-Schaltfläche für den internen Bereich.
 * Das Dateifeld liegt versteckt hinter der Schaltfläche; Tastaturbedienung
 * und Beschriftung bleiben erhalten. Rein visueller Baustein — die
 * Verarbeitung der Datei bleibt bei der aufrufenden Stelle.
 */
export function AdminUploadButton({
  label,
  accept,
  multiple = false,
  disabled = false,
  uploading = false,
  currentName,
  hint,
  ariaLabel,
  onFiles,
  children,
}: {
  /** Beschriftung, z. B. „Datei hochladen“ oder „Bild ersetzen“. */
  label: string;
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  uploading?: boolean;
  /** Name der bereits hinterlegten bzw. gewählten Datei. */
  currentName?: string | null;
  /** Formathinweis unter der Schaltfläche. */
  hint?: ReactNode;
  ariaLabel?: string;
  onFiles: (files: FileList) => void;
  /** Zusätzliche Aktionen neben der Schaltfläche (z. B. „Entfernen“). */
  children?: ReactNode;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const id = useId();

  return (
    <div className="space-y-2">
      <div
        className="flex flex-wrap items-center gap-2"
        onDragOver={(e) => {
          if (disabled || uploading) return;
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          if (disabled || uploading) return;
          e.preventDefault();
          setDragOver(false);
          if (e.dataTransfer.files?.length) onFiles(e.dataTransfer.files);
        }}
      >
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={disabled || uploading}
          aria-controls={id}
          className={
            "inline-flex items-center gap-2 rounded-md border bg-background px-3 py-2 text-xs font-semibold transition-colors hover:bg-secondary disabled:opacity-60 " +
            (dragOver ? "border-primary" : "border-border")
          }
        >
          {uploading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          ) : (
            <Upload className="h-3.5 w-3.5" aria-hidden />
          )}
          {uploading ? "Hochladen …" : label}
        </button>
        {children}
      </div>

      {currentName ? (
        <p className="text-xs text-muted-foreground">
          Hinterlegt: <strong className="font-semibold text-foreground">{currentName}</strong>
        </p>
      ) : null}
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}

      <input
        id={id}
        ref={inputRef}
        type="file"
        {...(accept ? { accept } : {})}
        multiple={multiple}
        aria-label={ariaLabel ?? label}
        className="hidden"
        disabled={disabled || uploading}
        onChange={(e) => {
          const files = e.target.files;
          if (files && files.length > 0) onFiles(files);
          e.target.value = "";
        }}
      />
    </div>
  );
}
